import { useState } from "react";
import { createPortal } from "react-dom";
import { Download, Share2, X } from "lucide-react";
import { toast } from "sonner";
import { LOGO } from "@/lib/images";

const TIER_LABELS = { day: "1 DAY", week: "1 WEEK", "15day": "15 DAYS", month: "1 MONTH", "3month": "3 MONTHS", lifetime: "LIFETIME", instant: "INSTANT" };

const tagFor = (sid) => `#${(sid || "XXXXXX").slice(0, 6).toUpperCase()}`;

function loadLogo() {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null); // text-only fallback if CDN blocks CORS
    img.src = LOGO;
  });
}

async function drawFlexCard(canvas, { product, tier, sid, dateStr }) {
  const W = 1080, H = 1080;
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  await document.fonts.ready;

  // background + faint grid
  ctx.fillStyle = "#000000";
  ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = "rgba(255,255,255,0.045)";
  ctx.lineWidth = 1;
  for (let x = 0; x <= W; x += 60) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke(); }
  for (let y = 0; y <= H; y += 60) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke(); }

  // frame + corner ticks
  ctx.strokeStyle = "rgba(255,255,255,0.35)";
  ctx.lineWidth = 2;
  ctx.strokeRect(42, 42, W - 84, H - 84);
  ctx.fillStyle = "#ffffff";
  [[42, 42], [W - 42, 42], [42, H - 42], [W - 42, H - 42]].forEach(([x, y]) => ctx.fillRect(x - 5, y - 5, 10, 10));

  // logo
  const img = await loadLogo();
  if (img) {
    const lw = 200;
    ctx.drawImage(img, W / 2 - lw / 2, 120, lw, lw * (img.height / img.width));
  }

  ctx.textAlign = "center";
  // headline
  ctx.fillStyle = "#ffffff";
  ctx.font = "700 128px Oswald, sans-serif";
  ctx.fillText("I JUST COPPED", W / 2, 470);
  // product
  ctx.font = "600 64px Oswald, sans-serif";
  ctx.fillStyle = "rgba(255,255,255,0.92)";
  ctx.fillText(product.toUpperCase(), W / 2, 590);
  // tier + tag
  ctx.font = "500 34px 'JetBrains Mono', monospace";
  ctx.fillStyle = "rgba(255,255,255,0.55)";
  ctx.fillText(`${TIER_LABELS[tier] || (tier || "").toUpperCase()}  //  ${tagFor(sid)}`, W / 2, 665);
  // date
  ctx.font = "400 28px 'JetBrains Mono', monospace";
  ctx.fillStyle = "rgba(255,255,255,0.35)";
  ctx.fillText(dateStr, W / 2, 725);

  // divider
  ctx.strokeStyle = "rgba(255,255,255,0.2)";
  ctx.beginPath(); ctx.moveTo(W / 2 - 220, 790); ctx.lineTo(W / 2 + 220, 790); ctx.stroke();

  // brand
  ctx.font = "700 56px Oswald, sans-serif";
  ctx.fillStyle = "#ffffff";
  ctx.fillText("GZ", W / 2, 890);
  ctx.font = "400 24px 'JetBrains Mono', monospace";
  ctx.fillStyle = "rgba(255,255,255,0.45)";
  ctx.fillText("// EXECUTE // INJECT // DOMINATE", W / 2, 940);
}

export default function FlexCard({ order, buttonTestId, compact = false }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const tierLabel = TIER_LABELS[order.tier] || (order.tier || "").toUpperCase();
  const dateStr = (() => {
    try { return new Date(order.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }); }
    catch { return ""; }
  })();

  const download = async () => {
    setBusy(true);
    try {
      const canvas = document.createElement("canvas");
      await drawFlexCard(canvas, { product: order.product_name, tier: order.tier, sid: order.session_id, dateStr });
      const a = document.createElement("a");
      a.href = canvas.toDataURL("image/png");
      a.download = `injectd-flex-${order.slug || "order"}.png`;
      a.click();
      toast.success("Flex card downloaded — drop it in the Discord");
    } catch {
      toast.error("Could not generate card — try again");
    }
    setBusy(false);
  };

  return (
    <>
      {compact ? (
        <button onClick={() => setOpen(true)} data-testid={buttonTestId} className="text-white/50 hover:text-white transition-colors" title="Flex this purchase">
          <Share2 size={16} />
        </button>
      ) : (
        <button onClick={() => setOpen(true)} data-testid={buttonTestId} className="btn-ghost px-6 py-3 text-xs inline-flex items-center gap-2">
          <Share2 size={14} /> FLEX YOUR COPY
        </button>
      )}

      {open && createPortal(
        <div className="fixed inset-0 z-[160] bg-black/90 backdrop-blur-sm flex items-center justify-center px-5" data-testid="flex-card-modal">
          <div className="relative w-full max-w-md">
            <button onClick={() => setOpen(false)} data-testid="flex-card-close" className="absolute -top-10 right-0 text-white/40 hover:text-white transition-colors" aria-label="Close">
              <X size={20} />
            </button>

            {/* live preview — mirrors the canvas layout */}
            <div className="relative aspect-square bg-black border border-white/30 overflow-hidden" data-testid="flex-card-preview">
              <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)", backgroundSize: "30px 30px" }} />
              <div className="absolute top-2 left-2 h-2 w-2 bg-white" /><div className="absolute top-2 right-2 h-2 w-2 bg-white" />
              <div className="absolute bottom-2 left-2 h-2 w-2 bg-white" /><div className="absolute bottom-2 right-2 h-2 w-2 bg-white" />
              <div className="relative h-full flex flex-col items-center justify-center px-8 text-center">
                <img src={LOGO} alt="" className="h-16 w-16 object-contain mb-6" style={{ filter: "drop-shadow(0 0 18px rgba(255,255,255,0.35))" }} />
                <div className="font-display text-4xl uppercase leading-none">I JUST COPPED</div>
                <div className="mt-3 font-display text-xl uppercase text-white/90 leading-tight">{order.product_name}</div>
                <div className="mt-2 font-mono text-[10px] tracking-[0.2em] text-white/55">{tierLabel}  //  {tagFor(order.session_id)}</div>
                <div className="mt-1 font-mono text-[9px] tracking-[0.15em] text-white/35">{dateStr}</div>
                <div className="my-4 h-px w-24 bg-white/20" />
                <div className="font-display text-lg tracking-[0.15em]">GZ</div>
                <div className="font-mono text-[8px] tracking-[0.25em] text-white/40 mt-1">{"// EXECUTE // INJECT // DOMINATE"}</div>
              </div>
            </div>

            <button onClick={download} disabled={busy} data-testid="flex-card-download" className="btn-inj mt-5 w-full flex items-center justify-center gap-2 px-8 py-4 text-sm disabled:opacity-60">
              <Download size={16} /> {busy ? "RENDERING..." : "DOWNLOAD PNG — DROP IT IN DISCORD"}
            </button>
            <div className="mt-3 text-center font-mono text-[9px] tracking-[0.25em] text-white/30">1080×1080 · SIZED FOR DISCORD &amp; TIMELINES</div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
